import {
  Directive,
  ElementRef,
  HostBinding,
  HostListener,
  Input,
  OnDestroy
} from '@angular/core';
import { createPopper, Instance, Placement } from '@popperjs/core';

@Directive({
  standalone: true,
  selector: '[eskPopover]'
})
export class EskPopoverDirective implements OnDestroy {
  @Input() eskPopoverTitle: string = 'Information';
  @Input() eskPopover!: string;
  @Input() eskPopoverPlacement: Placement = 'left';

  @HostBinding('attr.tabindex') tabindex = '0';
  @HostBinding('attr.role') role = 'button';

  private static activePopover?: EskPopoverDirective;

  private popoverElement?: HTMLElement;
  private popoverInstance?: Instance;
  private classForPlacement = new Map<Placement, string>([
    ['top', 'bs-popover-top'],
    ['bottom', 'bs-popover-bottom'],
    ['left', 'bs-popover-start'],
    ['right', 'bs-popover-end']
  ]);

  constructor(private element: ElementRef<HTMLElement>) {}

  @HostListener('click', ['$event'])
  onClick(event: MouseEvent) {
    event.stopPropagation();

    if (this.popoverElement) {
      this.closePopover();
      return;
    }

    this.openPopover();
  }

  @HostListener('keydown.escape')
  onEscape() {
    this.closePopover();
  }

  ngOnDestroy(): void {
    this.closePopover();
  }

  private getPlacementClass(): string {
    return (
      this.classForPlacement.get(this.eskPopoverPlacement) ?? 'bs-popover-start'
    );
  }

  openPopover() {
    if (this.popoverElement) {
      return;
    }

    if (
      EskPopoverDirective.activePopover &&
      EskPopoverDirective.activePopover !== this
    ) {
      EskPopoverDirective.activePopover.closePopover();
    }

    EskPopoverDirective.activePopover = this;

    this.popoverElement = document.createElement('div');
    this.popoverElement.className = `popover ${this.getPlacementClass()}`;

    const arrow = document.createElement('div');
    arrow.className = 'popover-arrow';

    const headerBlock = document.createElement('div');
    headerBlock.className = 'popover-header-block';

    const header = document.createElement('h3');
    header.className = 'popover-header';
    header.textContent = this.eskPopoverTitle;

    headerBlock.appendChild(header);

    const body = document.createElement('div');
    body.className = 'popover-body';
    body.innerHTML = this.eskPopover;

    this.popoverElement.appendChild(arrow);
    this.popoverElement.appendChild(headerBlock);
    this.popoverElement.appendChild(body);

    document.body.appendChild(this.popoverElement);

    this.popoverInstance = createPopper(
      this.element.nativeElement,
      this.popoverElement,
      {
        placement: this.eskPopoverPlacement,
        modifiers: [
          {
            name: 'offset',
            options: { offset: [0, 10] }
          },
          {
            name: 'arrow',
            options: {
              element: this.popoverElement.querySelector('.popover-arrow')
            }
          },
          {
            name: 'flip',
            options: {
              fallbackPlacements: ['top', 'bottom', 'right', 'left']
            }
          },
          {
            name: 'preventOverflow',
            options: {
              padding: 8
            }
          }
        ]
      }
    );

    document.addEventListener('click', this.handleDocumentClick);
  }

  closePopover() {
    document.removeEventListener('click', this.handleDocumentClick);

    this.popoverInstance?.destroy();
    this.popoverInstance = undefined;

    this.popoverElement?.remove();
    this.popoverElement = undefined;

    if (EskPopoverDirective.activePopover === this) {
      EskPopoverDirective.activePopover = undefined;
    }
  }

  private handleDocumentClick = (event: MouseEvent) => {
    event.stopPropagation();
    const target = event.target as Node;

    if (this.element.nativeElement.contains(target)) {
      return;
    }

    this.closePopover();
  };
}
